<template>
  <WdsButton
    ref="triggerBtn"
    secondary
    :disabled="isGenerating || missingRequiredFields"
    @click="generate"
  >
    <WdsStack align-center justify-center>
      <img v-if="isGenerating" src="/gifs/worksome-assistant-thinking.gif" style="height: 16px" />
      <WdsIcon v-else :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" :size="14" />
      <div class="m-l-5">
        {{ isGenerating ? $t('Generating...') : $t('Auto-fill from Overview') }}
      </div>
    </WdsStack>
  </WdsButton>
</template>

<script setup lang="ts">
import { WdsButton, WdsStack, WdsIcon, WdsIconNameEnum } from '@worksome/wds-components'
import axios from 'axios'
import { computed, ref } from 'vue'
import { useTranslation } from '@/composables/translation'
import { ExpectedExperienceLevel, Industry } from '@/gql/graphql'

const { $t } = useTranslation()
const isGenerating = ref(false)
const emit = defineEmits(['generate-metadata'])

const props = defineProps({
  jobId: {
    type: String,
    required: false,
    default: null,
  },
  title: {
    type: String,
    required: true,
    default: null,
  },
  description: {
    type: String,
    required: true,
    default: null,
  },
})

const missingRequiredFields = computed(
  () => !props.jobId || !props.title?.trim() || !props.description?.trim(),
)

export type GeneratedJobMetadataResponse = {
  seniority: ExpectedExperienceLevel[]
  skills: string[]
  industries: Industry[]
}

const generate = async () => {
  if (missingRequiredFields.value) return

  try {
    isGenerating.value = true

    const payload = {
      job_id: props.jobId,
      title: props.title,
      description: props.description,
    }

    const response = await axios.post('/web-api/ai/generate-job-metadata', payload)

    if (response.data) {
      emit('generate-metadata', {
        ...response.data,
        industries: response.data.industries.map(
          (industry: { id: string; name: string }) =>
            ({
              id: industry.id,
              name: industry.name,
            }) as Industry,
        ),
      } as GeneratedJobMetadataResponse)
    }
  } finally {
    isGenerating.value = false
  }
}
</script>
